import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/atoms/table";
import { Checkbox } from "~/atoms/checkbox";
import NoResult from "~/molecules/NoResult";
import { type BulkSelection } from "~/hooks/useBulkSelection";
import { cn } from "~/lib/utils";

export interface AnchoringColumn<TRow> {
  key: string;
  header: ReactNode;
  cell: (row: TRow) => ReactNode;
}

interface Props<TRow, TId> {
  columns: AnchoringColumn<TRow>[];
  rows: TRow[];
  getRowId: (row: TRow) => TId;
  emptyMessage: string;
  onRowClick?: (row: TRow) => void;
  selection?: Pick<
    BulkSelection<TId>,
    "selectedIds" | "allSelected" | "toggleRow" | "toggleAll"
  >;
  /** Rows for which this returns false render no selection checkbox. */
  isRowSelectable?: (row: TRow) => boolean;
}

/**
 * Lighter, column-driven table for the anchoring tabs — no filters sidebar,
 * column registry or virtualization, just the shared table atoms with optional
 * bulk row selection.
 */
export function AnchoringTable<TRow extends { title: string }, TId>({
  columns,
  rows,
  getRowId,
  emptyMessage,
  onRowClick,
  selection,
  isRowSelectable,
}: Props<TRow, TId>) {
  const { t } = useTranslation();

  if (rows.length === 0) {
    return <NoResult message={emptyMessage} className="py-8" />;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {selection && (
            <TableHead className="w-10">
              <Checkbox
                checked={selection.allSelected}
                onCheckedChange={selection.toggleAll}
                aria-label={t(($) => $.commons.buttons.selectAll)}
              />
            </TableHead>
          )}
          {columns.map((column) => (
            <TableHead key={column.key}>{column.header}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          const id = getRowId(row);
          return (
            <TableRow
              key={String(id)}
              className={cn(onRowClick && "cursor-pointer")}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {selection && (
                <TableCell>
                  {(!isRowSelectable || isRowSelectable(row)) && (
                    <Checkbox
                      checked={selection.selectedIds.has(id)}
                      onClick={(event) => event.stopPropagation()}
                      onCheckedChange={() => selection.toggleRow(id)}
                      aria-label={t(($) => $.commons.buttons.selectItem, {
                        name: row.title,
                      })}
                    />
                  )}
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell key={column.key}>{column.cell(row)}</TableCell>
              ))}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
